import { useCallback, useEffect, useMemo, useState } from "react";
import Pagination from "../../LandingPages/Pagination";
import { CONTRACT_STATUS_DESC } from "../../../constants/Contract";
import ApplicationSentToCandidate from "./SentToCandidatePage";
import HomePageActiveApps from "./HomePageActiveApps";
import HomePagePriorApps from "./HomePagePriorApps";
import { uploadedReqDocsActions } from "../../../store/slice/uploadedDocs";
import DocConfirmationModal from "./UploadDocument/DocConfirmationModal";
import { useDispatch } from "react-redux";
import {
  getCandidateReqDocumentStatuses,
  getCandidateDocumentStatuses,
} from "../../../api/FilenetDocuments";
import CandidateDocModal from "./UploadDocument/CandidateDocModal";
import Loader from "../../SharedComponents/Loader";
import UploadDocument from "./UploadDocument";
import { get } from "underscore";

const RECORDS_PER_PAGE = 5;

function ActiveAndPriorApplications({
  contractList,
  archiveList,
  mmid,
  oktasubid,
  hasSSN = false,
}) {
  const [currentPage, setCurrentPage] = useState(1);
  const [showGetStartedPage, setShowGetStartedePage] = useState(false);
  const [selectedContractData, setSelectedContractData] = useState({});
  const [selectedContractDataUpload, setSelectedContractDataUpload] = useState(
    {},
  );
  const [showFormsModal, setShowFormsModal] = useState(false);
  const [refetchDocStatus, setRefetchDocStatus] = useState(false);
  const [isDocReqUploadModalOpen, setIsDocReqUploadModalOpen] = useState(false);
  const [docReqUploadModalData, setDocReqUploadModalData] = useState({
    oktaSubId: "",
    contractid: "",
    contracttypedesc: "",
    isRequested: true,
  });
  const [showSubmittedModal, setShowSubmittedModal] = useState(false);

  const dispatch = useDispatch();

  const [candidateDocStatusesLoading, setCandidateDocStatusesLoading] =
    useState(false);

  const fetchCandidateDocStatuses = useCallback(async () => {
    try {
      setCandidateDocStatusesLoading(true);
      const reqStatuses = await getCandidateReqDocumentStatuses(oktasubid);

      if (reqStatuses?.results) {
        dispatch(uploadedReqDocsActions.setRequestedDocs(reqStatuses.results));
      } else {
        dispatch(uploadedReqDocsActions.resetReqDocs());
      }

      const statuses = await getCandidateDocumentStatuses(oktasubid);

      if (statuses?.results) {
        dispatch(uploadedReqDocsActions.setUploadedDocs(statuses.results));
      } else {
        dispatch(uploadedReqDocsActions.resetUploadedDocs());
      }
    } catch (err) {
      console.error("Failed to fetch candidate req. document statuses:", err);
    }
    setCandidateDocStatusesLoading(false);
    setRefetchDocStatus(false);
  }, [oktasubid, dispatch]);

  // Initial fetch on mount
  useEffect(() => {
    fetchCandidateDocStatuses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch when refetchDocStatus is set to true
  useEffect(() => {
    if (refetchDocStatus) {
      fetchCandidateDocStatuses();
    }
  }, [refetchDocStatus, fetchCandidateDocStatuses]);

  const toggleFormsModal = () => {
    if (showFormsModal) {
      setSelectedContractData({});
    }
    setShowFormsModal(!showFormsModal);
  };

  const setContractData = (contractJSON) => {
    setSelectedContractData(contractJSON);
    toggleFormsModal();
  };

  const setContractDataUpload = (data) => {
    setSelectedContractDataUpload(data);
  };

  const defaultGetStartedContract = useMemo(() => {
    return contractList.length === 1 &&
      archiveList.length === 0 &&
      contractList[0].contractstatusdesc ===
        CONTRACT_STATUS_DESC.INVITATION_SENT
      ? contractList[0]
      : null;
  }, [contractList, archiveList]);

  const showGetStartedFor = useCallback(
    (contractId) => {
      setShowGetStartedePage(true);
      setGetStartedContract(
        contractList.find((contract) => contract.contractid === contractId),
      );
    },
    [contractList],
  );

  const toggleGetStarted = (
    contractId = defaultGetStartedContract?.contractid,
  ) => {
    setGetStartedContract(contractId);
    setShowGetStartedePage(!showGetStartedPage);
  };

  const [getStartedContract, setGetStartedContract] = useState(null);

  useEffect(() => {
    if (defaultGetStartedContract) {
      setGetStartedContract(defaultGetStartedContract?.contractid);
      showGetStartedFor(defaultGetStartedContract?.contractid);
    }
  }, [defaultGetStartedContract, showGetStartedFor]);

  const handlePaginationChange = (id) => {
    if (id === "nextPage") {
      setCurrentPage(currentPage + 1);
    } else if (id === "lastPage") {
      setCurrentPage(currentPage - 1);
    } else {
      setCurrentPage(id);
    }
  };

  const indexOfLastRecord = currentPage * RECORDS_PER_PAGE;
  const indexOfFirstRecord = indexOfLastRecord - RECORDS_PER_PAGE;
  const currentRecords = archiveList.slice(
    indexOfFirstRecord,
    indexOfLastRecord,
  );
  const nPages = Math.ceil(archiveList.length / RECORDS_PER_PAGE);
  const pageNumbers = [...Array(nPages + 1).keys()].slice(1);

  return (
    <>
      {showGetStartedPage ? (
        <div className="container">
          <ApplicationSentToCandidate
            contract={getStartedContract.contracttypedesc}
            agency={getStartedContract.agencyname}
            contractid={getStartedContract.contractid}
            agencynumber={getStartedContract.agencynumber}
            nyregs={getStartedContract.nyregs}
            phonenumber={getStartedContract.phonenumber}
            mmlis={getStartedContract.mmlis}
            financingonly={getStartedContract.financingonly}
            multiContracts={contractList.length}
            archiveContract={archiveList.length}
            toggleGetStarted={toggleGetStarted}
            cria={getStartedContract?.cria}
            ia={getStartedContract?.ia}
          />

          <p className="need-help-box">
            Need help? Contact your Agency Licensing Coordinator or Agency
            Supervisory Officer at Agency #{getStartedContract.agencynumber} at{" "}
            {getStartedContract.phonenumber}
          </p>
        </div>
      ) : (
        <>
          <div className="container">
            {mmid && <h2 id="mmidSection">MMID {mmid}</h2>}
            <div>
              <h4>
                Active Applications ({contractList.length})
                <div className="tooltipContainer">
                  <span className="icon-info"></span>
                  <span className="tooltiptext">
                    This section contains applications submitted through this
                    system that are still currently in progress.
                  </span>
                </div>
              </h4>

              <hr></hr>

              {contractList.map((x, idx) => (
                <HomePageActiveApps
                  key={`${idx}_${x.contractid}`}
                  agencynumber={x.agencynumber}
                  applicationStatus={x.contractstatusdesc}
                  contract={x}
                  contracttype={x.contracttypedesc}
                  contractid={x.contractid}
                  mmlis={x.mmlis}
                  finance={x.financingonly}
                  oktasubid={x.oktasubid}
                  lastchangedate={x.lastchangedate}
                  showGetStartedFor={showGetStartedFor}
                  multiContracts={contractList.length}
                  cria={x?.cria}
                  ia={x?.ia}
                  setContractData={setContractData}
                  candidateDocStatusesLoading={candidateDocStatusesLoading}
                  onDocumentsUploaded={() => setRefetchDocStatus(true)}
                  isDocReqUploadModalOpen={isDocReqUploadModalOpen}
                  docReqUploadModalData={docReqUploadModalData}
                  onShowConfirmationModal={() => setShowSubmittedModal(true)}
                  hasSSN={hasSSN}
                />
              ))}
            </div>
          </div>
          {archiveList.length > 0 && (
            <div className="container" style={{ marginTop: "100px" }}>
              <div>
                <h4>
                  Prior Applications ({archiveList.length})
                  <div className="tooltipContainer">
                    <span className="icon-info"></span>
                    <span className="tooltiptext">
                      This section contains applications submitted through this
                      system that might not be active contracts or agreements.{" "}
                      <br></br> If you don’t see the application that you are
                      looking for, reach out to the agency that you submitted it
                      with.
                    </span>
                  </div>
                </h4>
                <hr></hr>
                <>
                  {currentRecords.map((x, idx) => (
                    <HomePagePriorApps
                      key={`${idx}_${x.contractid}`}
                      agencynumber={x.agencynumber}
                      agency={x.agencyname}
                      applicationStatus={x.contractstatusdesc}
                      contract={x}
                      contracttype={x.contracttypedesc}
                      contracteffectivedate={x.contracteffectivedate}
                      contractid={x.contractid}
                      prevSub={x.prevsub}
                      firstname={x.firstname}
                      lastname={x.lastname}
                      nyregs={x.nyregs}
                      phonenumber={x.phonenumber}
                      mmlis={x.mmlis}
                      finance={x.financingonly}
                      cmid={x.cmid}
                      lastchangedate={x.lastchangedate}
                      mmlisregistrationdate={x.mmlisregistrationdate}
                      oktasubid={x.oktasubid}
                      setContractData={setContractData}
                      showviewconfidential={x.showviewconfidential}
                      cria={x?.cria}
                      ia={x?.ia}
                      isPriorContract={true}
                      isDocReqUploadModalOpen={isDocReqUploadModalOpen}
                      docReqUploadModalData={docReqUploadModalData}
                    />
                  ))}
                  {archiveList.length > 5 && (
                    <Pagination
                      handleChange={handlePaginationChange}
                      nPages={nPages}
                      currentPage={currentPage}
                      pageNumbers={pageNumbers}
                    />
                  )}
                </>
              </div>
            </div>
          )}
        </>
      )}

      {candidateDocStatusesLoading ? <Loader /> : null}

      {isDocReqUploadModalOpen && docReqUploadModalData?.contractid ? (
        <UploadDocument
          docReqUploadModalData={docReqUploadModalData}
          oktaSubId={docReqUploadModalData.oktaSubId}
          contractid={docReqUploadModalData?.contractid}
          contracttype={docReqUploadModalData?.contracttypedesc}
          isRequested
          isDocReqUploadModalOpen={isDocReqUploadModalOpen}
          onDocumentsUploaded={() => setRefetchDocStatus(true)}
          setIsDocReqUploadModalOpen={setIsDocReqUploadModalOpen}
          onShowConfirmationModal={() => setShowSubmittedModal(true)}
        />
      ) : null}

      {selectedContractDataUpload && selectedContractDataUpload?.contractid ? (
        <UploadDocument
          docReqUploadModalData={docReqUploadModalData}
          oktaSubId={selectedContractDataUpload?.oktasubid}
          contractid={selectedContractDataUpload?.contractid}
          contracttype={selectedContractDataUpload?.contracttype}
          isRequested={selectedContractDataUpload?.isRequested}
          isDropdownItem={selectedContractDataUpload?.isDropdownItem}
          isDocReqUploadModalOpen={selectedContractDataUpload?.isDropdownItem}
          onDocumentsUploaded={() => setRefetchDocStatus(true)}
          setIsDocReqUploadModalOpen={setIsDocReqUploadModalOpen}
          setContractDataUpload={setContractDataUpload}
          onShowConfirmationModal={() => setShowSubmittedModal(true)}
        />
      ) : null}

      <DocConfirmationModal
        isOpen={showSubmittedModal}
        onClose={() => setShowSubmittedModal(false)}
      />

      <CandidateDocModal
        {...selectedContractData}
        isOpen={showFormsModal}
        source={selectedContractData?.applicationStatus}
        contractid={selectedContractData?.contractid}
        candidateid={
          contractList.find((c) => c.candidateid)?.candidateid || null
        }
        oktasubid={oktasubid}
        contracttypedesc={selectedContractData?.contracttypedesc}
        onClose={toggleFormsModal}
        setIsDocReqUploadModalOpen={setIsDocReqUploadModalOpen}
        setDocReqUploadModalData={setDocReqUploadModalData}
      />
    </>
  );
}

export default ActiveAndPriorApplications;
